import { Component, Input, OnInit, ViewEncapsulation } from "@angular/core";
import { Task } from "@task-utils/types";
import { jobs_management_translations } from "./jobs.trans";
import { color, ConfirmDialogComponent, date, Frame, language, modal, notification, utils } from "curvy";
import { User } from "@task-modules/users";
import { TaskRoutes } from "@task-utils/routes";
import { ActivatedRoute } from "@angular/router";
import { AddWorkingHoursDialog } from "./add-working-hours.dialog";


@Component({
   selector: 'task-temp-staff-applications',
   templateUrl: './temp-staff-applications.component.html',
   styles: [`
      task-temp-staff-applications {
         display: flex;
         flex-flow: column nowrap;
         overflow: hidden !important;
      }
   `],
   styleUrls: ['../../core/components/components.scss'],
   encapsulation: ViewEncapsulation.None,
})

export class TempStaffApplicationsComponent implements OnInit {
   constructor(private ar: ActivatedRoute) {
      language.load_translations(jobs_management_translations);
   }

   @Input()
   request: Task.TempStaffRequest

   applications: Task.TempStaffApplication[] = [];
   plans: Map<number, Task.TempStaffPlan[]> = new Map();
   user: User.Current | null = null;
   can_accept = false;
   can_add_working_hours = false;

   paginator: {
      page_no: number;
      page_size: number;
      count: number;
   } = {
      page_no: 1,
      page_size: 25,
      count: 0,
   }

   max_page_no: number;

   ngOnInit(): void {
      this.initialize();
   }

   status() {
      let status_name = "x";
      let status_icon = "x";
      let status_color = "x";

      let applications_over = new Date() >= new Date(this.request.date_from);
      let work_over = new Date() > new Date(this.request.date_to);
      let candidates_selected = this.request.accepted_count < this.request.number_of_workers;

      if (!applications_over) {
         if (candidates_selected) {
            status_name = "TEMP_STAFF_REQUEST.STATUS.APPLICATIONS_OPEN";
            status_icon = "assignment";
            status_color = Task.STATUS_COLORS.YELLOW;

         } else {
            status_name = "TEMP_STAFF_REQUEST.STATUS.APPLICATIONS_FINISHED";
            status_icon = "assignment_turned_in";
            status_color = Task.STATUS_COLORS.BRIGHT_YELLOW;
         }

      } else if (!work_over) {
         status_name = "TEMP_STAFF_REQUEST.STATUS.WORKS_UNDERWAY";
         status_icon = "event";
         status_color = Task.STATUS_COLORS.BLUE;

      } else {
         status_name = "TEMP_STAFF_REQUEST.STATUS.WORKS_FINISHED";
         status_icon = "check_circle";
         status_color = Task.STATUS_COLORS.ORANGE;
      }

      return { name: status_name, icon: status_icon, color: status_color };
   }

   async initialize() {
      this.user = await User.currentUserPromise;

      if (this.request == null) {
         const request_id = +this.ar.snapshot.paramMap.get("id");


         Frame.set({
            title: language.translate("NAVROUTE.APPLICATIONS", request_id),
            visible: true,
            layout: "top-middle",
            size: "scroll"
         });

         try {
            const res = await TaskRoutes.temp_staff_requests.api_temp_staff_request_get_single(request_id);
            this.request = res.data;
            await this.load_applications();

         } catch (error) {
            console.error(error);
            utils.router.navigateByUrl('/temp-staff-requests');
         }
      }

      this.can_accept = this.user.user_type_id === Task.USER_TYPE.ADMIN
         || this.user.user_type_id === Task.USER_TYPE.SYSTEM_ADMIN
         || this.user.user_type_id === Task.USER_TYPE.STORE_MANAGER;

      this.can_add_working_hours = this.can_accept
         && new Date(this.request.date_to).getTime() >= new Date().getTime();

      this.load_applications();
   }

   async load_applications() {
      TaskRoutes.temp_staff_requests.api_temp_staff_applications_search(
         this.request.temp_staff_request_id, {
            search: [],
            attributes: [],
            sort: [
               {attribute: "#:accepted", direction: "desc"},
               {attribute: "created_at", direction: "desc"},
            ],
         },
         this.paginator
      ).then(res => {
         this.applications = res.data;
         this.paginator.count = res.count;
         this.max_page_no = Math.ceil(res.count/this.paginator.page_size);

         let accepted = this.applications.filter(a => a.accepted);
         TaskRoutes.temp_staff_requests.api_temp_staff_plans_search(
            {
               search: [
                  accepted.map(a => a.temp_staff_application_id) as any,
                  new Date().toISOString()
               ],
               attributes: [
                  "U:temp_staff_application_id",
                  "-d:planned_end_time"
               ],
            },
            {
               page_no: 1,
               page_size: 1000
            }
         ).then(res => {
            for (let plan of res.data) {
               let plan_arr = this.plans.get(plan.temp_staff_application_id);

               if (!plan_arr) {
                  plan_arr = [];
                  this.plans.set(plan.temp_staff_application_id, plan_arr);
               }

               let idx = plan_arr.findIndex(p => p.temp_staff_plan_id == plan.temp_staff_plan_id);
               if (idx >= 0) {
                  plan_arr[idx] = plan;
               } else {
                  plan_arr.push(plan);
               }
            }

            for (let plan_arr of this.plans.values()) {
               plan_arr.sort((a, b) => {
                  return +new Date(a.planned_start_time) - +new Date(b.planned_start_time);
               });
            }
         });
      });
   }

   get_dates() {
      if (this.request.date_from && this.request.date_to) {
         let from = new Date(this.request.date_from)
         let to = new Date(this.request.date_to)


         if (from.getTime() === to.getTime()) {
            return date.get_formatted_date(from, 'short_date');
         } else {
            return `${date.get_formatted_date(from, 'short_date')} - ${date.get_formatted_date(to, 'short_date')}`;
         }
      }
      return '';
   }

   change_page(dir:number) {
      if (dir==1) {
         this.paginator.page_no= Math.max(this.paginator.page_no++, this.max_page_no);
         this.load_applications();
      }
      else {
         this.paginator.page_no = Math.min(this.paginator.page_no--, 1);
         this.load_applications();
      }
   }

   async accept_application(id: number) {
      modal.open(
         ConfirmDialogComponent, async (succ) => {
            if (succ) {
               let res = await TaskRoutes.temp_staff_requests.api_temp_staff_application_accept(id);
               if (res.status == "403") {
                  notification.show({
                     title: language.translate("ERROR"),
                     message: language.translate("TEMP_STAFF_REQUEST.APPLICATION.NO_PERMISSION_TO_MODIFY")
                  })
               }

               this.paginator.page_no = 1;
               this.load_applications();
            }
         }, {
            title: language.translate("APPLICATION.ACCEPT.TITLE"),
            message: language.translate("APPLICATION.ACCEPT.MESSAGE"),
            iconColor: color.variable(color.Variable.warn)
         }
      )
   }

   async add_working_hours(application_id: number) {
      let start_date = new Date(this.request.date_from);
      let end_date = new Date(this.request.date_to);

      modal.open(
         AddWorkingHoursDialog, async (succ) => {
            if (succ) {
               notification.show({
                  title: language.translate("SUCCESS"),
                  message: language.translate("TEMP_STAFF_REQUEST.APPLICATION.WORKING_HOURS_ADDED")
               });
               this.load_applications();
            }
         },
         {
            application_id,
            start_date,
            end_date
         }
      );
   }
}
